import React, { useState, useEffect, Fragment, useRef, useMemo } from "react";
import { Task } from "../types";
import { Transition, Dialog } from "@headlessui/react";
import {
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { HiOutlineAdjustments } from "react-icons/hi";
import { taskService, CATEGORY_COLORS } from "../services/database";
import { DndContext, closestCenter, rectIntersection, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent, DragOverEvent, DragStartEvent, useDroppable, DragOverlay } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import SortableTaskItem from "./SortableTaskItem";
import CategoryFilterPopup from "./CategoryFilterPopup";
import CategoryModal from "./CategoryModal";
import DueDateSelector from "./DueDateSelector";
import { usePostHog } from 'posthog-js/react';

// Define container IDs
const TODAY_CONTAINER_ID = 'today-container';
const QUEUE_CONTAINER_ID = 'queue-container';

// --- Droppable Container Wrapper --- 
interface DroppableContainerProps {
  id: string;
  children?: React.ReactNode;
  className?: string; // Allow passing className
}

const DroppableContainer: React.FC<DroppableContainerProps> = ({ id, children, className }) => {
  const { setNodeRef } = useDroppable({ id });
  
  return (
    <div ref={setNodeRef} className={className}>
      {children}
    </div>
  );
};
// -----------------------------------

type DragFeedbackReason = 'none' | 'active-task-locked' | 'today-limit-reached';

const DragFeedbackTooltip = ({ reason }: { reason: DragFeedbackReason }) => {
  if (reason === 'none') return null;
  
  const messages = {
    'active-task-locked': 'Active task cannot be moved to Upcoming',
    'today-limit-reached': 'Today list is full (5 tasks max)'
  };

  return (
    <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 z-50 
                    px-3 py-1.5 rounded-full text-sm font-medium text-white 
                    bg-red-500/90 backdrop-blur-sm shadow-lg whitespace-nowrap
                    animate-in fade-in slide-in-from-top-1 duration-200 pointer-events-none">
      {messages[reason]}
    </div>
  );
};

interface TaskListProps {
  tasks: Task[]; // Represents ALL tasks
  onAddTask: (title: string, category?: string, dueDate?: Date | null) => Promise<number | void>;
  onToggleTask: (taskId: number) => Promise<void>;
  onDeleteTask: (id: number) => Promise<void>;
  onUpdateTask: (id: number, text: string, category?: string) => Promise<void>;
  sessionType: "work" | "shortBreak";
  onTasksReorder: (tasks: Task[]) => void; // Still needed for intra-list reorder (later)
  onSetTaskTodayStatus: (taskId: number, isToday: boolean) => Promise<Task[] | undefined>;
  onRefreshTasks?: () => Promise<Task[]>;
  activeTaskId: number | null;
  onSelect: (taskId: number | null) => void;
  isTimerRunning: boolean;
  onActiveTaskMovedToUpcoming?: (movedTaskId: number, updatedTasks?: Task[]) => Promise<void> | void;
}

const TaskList: React.FC<TaskListProps> = ({
  tasks: allTasks, // Rename prop for clarity
  onAddTask,
  onToggleTask,
  onDeleteTask,
  onUpdateTask,
  sessionType,
  onTasksReorder, // Keep prop, but won't call it for inter-list drags for now
  onSetTaskTodayStatus,
  onRefreshTasks,
  activeTaskId,
  onSelect,
  isTimerRunning,
  onActiveTaskMovedToUpcoming,
}) => {
  const posthog = usePostHog();
  
  // State for the inline add form
  const [recentlyAdded, setRecentlyAdded] = useState<number | null>(null);
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState("");
  const [editingCategory, setEditingCategory] = useState<string | undefined>(undefined);
  const [editingDueDate, setEditingDueDate] = useState<Date | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<number | null>(null);
  const [inlineAdding, setInlineAdding] = useState(false);
  const [inlineNewTask, setInlineNewTask] = useState("");
  const [inlineNewCategory, setInlineNewCategory] = useState<string | undefined>(undefined);
  const [inlineNewDueDate, setInlineNewDueDate] = useState<Date | null>(null);
  const [filterCategory, setFilterCategory] = useState<string | undefined>(undefined);
  const [categoryPopupRefreshKey, setCategoryPopupRefreshKey] = useState(0);
  const [activeDragId, setActiveDragId] = useState<string | number | null>(null);
  const [inlineAddingToday, setInlineAddingToday] = useState(false);
  const findLastIndex = <T,>(items: T[], predicate: (value: T, index: number) => boolean): number => {
    for (let index = items.length - 1; index >= 0; index -= 1) {
      if (predicate(items[index], index)) {
        return index;
      }
    }
    return -1;
  };
  const [inlineNewTodayTask, setInlineNewTodayTask] = useState("");
  const [inlineNewTodayCategory, setInlineNewTodayCategory] = useState<string | undefined>(undefined);
  const [inlineNewTodayDueDate, setInlineNewTodayDueDate] = useState<Date | null>(null);
  const [isDraggingOverFullToday, setIsDraggingOverFullToday] = useState(false);
  const [isDraggingActiveToUpcoming, setIsDraggingActiveToUpcoming] = useState(false);
  const [shouldShakeDragItem, setShouldShakeDragItem] = useState(false);
  const [dragFeedbackReason, setDragFeedbackReason] = useState<DragFeedbackReason>('none');
  const [dragStartContainer, setDragStartContainer] = useState<string | null>(null);

  const inlineInputRef = useRef<HTMLTextAreaElement>(null);
  const inlineTodayInputRef = useRef<HTMLTextAreaElement>(null);
  const shakeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-grow helper for multi-line task inputs
  const autoGrow = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Hybrid collision detection: prefer intersection for containers, fallback to center for item reordering
  const collisionDetectionStrategy = (args: any) => {
    const pointerIntersections = rectIntersection(args);
    if (pointerIntersections.length > 0) {
      const nonContainerHits = pointerIntersections.filter(
        (intersection) =>
          intersection.id !== TODAY_CONTAINER_ID && intersection.id !== QUEUE_CONTAINER_ID
      );

      if (nonContainerHits.length > 0) {
        return nonContainerHits;
      }

      return pointerIntersections;
    }
    return closestCenter(args);
  };

  // Reset recently added task after animation
  useEffect(() => {
    if (recentlyAdded) {
      const timer = setTimeout(() => {
        setRecentlyAdded(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [recentlyAdded]);

  // Focus on the inline input when it appears
  useEffect(() => {
    if (inlineAdding && inlineInputRef.current) {
      inlineInputRef.current.focus();
      autoGrow(inlineInputRef.current);
    }
  }, [inlineAdding]);

  // Keep inline input height in sync with content
  useEffect(() => {
    autoGrow(inlineInputRef.current);
  }, [inlineNewTask]);

  // Focus on the inline Today input when it appears
  useEffect(() => {
    if (inlineAddingToday && inlineTodayInputRef.current) {
      inlineTodayInputRef.current.focus();
      autoGrow(inlineTodayInputRef.current);
    }
  }, [inlineAddingToday]);

  // Keep inline Today input height in sync with content
  useEffect(() => {
    autoGrow(inlineTodayInputRef.current);
  }, [inlineNewTodayTask]);

  // Cleanup shake timeout on unmount
  useEffect(() => {
    return () => {
      if (shakeTimeoutRef.current) {
        clearTimeout(shakeTimeoutRef.current);
      }
    };
  }, []);



  // Show inline input when queue is empty (no queued tasks), unless a category filter is active
  useEffect(() => {
    const queueEmpty = allTasks.filter(t => !t.isToday && !t.completed && (!filterCategory || t.category === filterCategory)).length === 0;
    setInlineAdding(queueEmpty && !filterCategory);
  }, [allTasks, filterCategory]);

  // Utility function to get category color classes
  const getCategoryStyle = (category: string | undefined) => {
    if (!category) return { bg: 'bg-gray-100 dark:bg-gray-900/20', text: 'text-gray-700 dark:text-gray-300' };
    
    if (category === "Uncategorized") {
      return {
        bg: 'bg-gray-200 dark:bg-gray-700',
        text: 'text-gray-800 dark:text-gray-200'
      };
    }
    
    const color = CATEGORY_COLORS[category] || 'gray';
    const bgClasses = {
      blue: 'bg-blue-100 dark:bg-blue-900/20',
      green: 'bg-green-100 dark:bg-green-900/20',
      red: 'bg-red-100 dark:bg-red-900/20',
      purple: 'bg-purple-100 dark:bg-purple-900/20',
      yellow: 'bg-yellow-100 dark:bg-yellow-900/20',
      gray: 'bg-gray-100 dark:bg-gray-900/20',
    };
    const textClasses = {
      blue: 'text-blue-700 dark:text-blue-300',
      green: 'text-green-700 dark:text-green-300',
      red: 'text-red-700 dark:text-red-300',
      purple: 'text-purple-700 dark:text-purple-300',
      yellow: 'text-yellow-700 dark:text-yellow-300',
      gray: 'text-gray-700 dark:text-gray-300',
    };
    return {
      bg: bgClasses[color as keyof typeof bgClasses] || bgClasses.gray,
      text: textClasses[color as keyof typeof textClasses] || textClasses.gray,
    };
  };



  const handleCheckboxChange = async (taskId: number) => {
    // Find task in all tasks to handle completion correctly
    const task = allTasks.find((t) => t.id === taskId);
    if (!task) return;

    try {
      // Track task completion (only when completing, not uncompleting)
      if (!task.completed) {
        posthog?.capture('task_completed', {
          had_category: !!task.category,
          had_due_date: !!task.dueDate,
          days_since_created: task.createdAt 
            ? Math.floor((Date.now() - new Date(task.createdAt).getTime()) / (1000 * 60 * 60 * 24))
            : null
        });
      }
      
      await onToggleTask(taskId);
      // Task completion might affect its list (today/queue), state updates via useEffect[allTasks]
    } catch (error) {
      console.error("Failed to toggle task completion:", error);
    }
  };


  const handleAddInlineTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (inlineNewTask.trim()) {
      const newId = await onAddTask(inlineNewTask.trim(), inlineNewCategory, inlineNewDueDate);
      if (typeof newId === 'number') {
        setRecentlyAdded(newId); // Trigger animation if ID is returned
        
        // Track task creation
        posthog?.capture('task_created', {
          has_category: !!inlineNewCategory,
          has_due_date: !!inlineNewDueDate,
          source: 'upcoming_quick_add'
        });
      }
      setInlineNewTask("");
      setInlineNewCategory(undefined);
      setInlineNewDueDate(null);
      setInlineAdding(false);
      
      // Trigger refresh to ensure filter popup updates with new categories
      setCategoryPopupRefreshKey(prev => prev + 1);
    }
  };

  const handleCancelInlineAdd = () => {
    // Reset task text, category, and due date when canceling
    setInlineAdding(false);
    setInlineNewTask("");
    setInlineNewCategory(undefined);
    setInlineNewDueDate(null);
  };

  const handleAddTodayTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (inlineNewTodayTask.trim()) {
      // Check if Today already has 5 non-completed tasks
      const todayTaskCount = allTasks.filter(t => t.isToday && !t.completed).length;
      if (todayTaskCount >= 5) {
        // Could show a toast notification here
        return;
      }
      
      const newTaskId = await onAddTask(inlineNewTodayTask.trim(), inlineNewTodayCategory, inlineNewTodayDueDate);
      if (newTaskId && typeof newTaskId === 'number') {
        // Mark the task as today
        await onSetTaskTodayStatus(newTaskId, true);
        setRecentlyAdded(newTaskId);
        
        // Track task creation
        posthog?.capture('task_created', {
          has_category: !!inlineNewTodayCategory,
          has_due_date: !!inlineNewTodayDueDate,
          source: 'today_quick_add'
        });
      }
      setInlineNewTodayTask("");
      setInlineNewTodayCategory(undefined);
      setInlineNewTodayDueDate(null);
      setInlineAddingToday(false);
    }
  };

  const handleCancelTodayAdd = () => {
    setInlineAddingToday(false);
    setInlineNewTodayTask("");
    setInlineNewTodayCategory(undefined);
    setInlineNewTodayDueDate(null);
  };

  const initiateDelete = (taskId: number) => {
    if (taskId === -1) {
      // Delete completed tasks logic needs to consider *all* tasks
      const completedTaskIds = allTasks.filter(task => task.completed).map(task => task.id);
      if (completedTaskIds.length > 0) {
        setTaskToDelete(-1); // Use -1 to indicate deleting all completed tasks
        setShowDeleteConfirm(true);
      }
    } else {
      setTaskToDelete(taskId);
      setShowDeleteConfirm(true);
    }
  };

  const confirmDelete = async () => {
    let needsRefresh = false;
    if (taskToDelete === -1) {
      // Delete all completed tasks from the source
      const completedTasks = allTasks.filter(task => task.completed);
      for (const task of completedTasks) {
        await onDeleteTask(task.id);
      }
      needsRefresh = completedTasks.length > 0;
      
      // Track bulk delete
      if (completedTasks.length > 0) {
        posthog?.capture('task_deleted', {
          count: completedTasks.length,
          source: 'bulk_delete_completed'
        });
      }
    } else if (taskToDelete !== null) {
      // Delete single task
      const task = allTasks.find(t => t.id === taskToDelete);
      await onDeleteTask(taskToDelete);
      needsRefresh = true;
      
      // Track single delete
      posthog?.capture('task_deleted', {
        count: 1,
        was_completed: task?.completed || false,
        source: 'single_delete'
      });
    }

    setShowDeleteConfirm(false);
    setTaskToDelete(null);

    // Refresh tasks from the source if a delete operation happened
    if (needsRefresh && onRefreshTasks) {
        await onRefreshTasks();
        // Let the useEffect handle separation, just trigger reorder if needed
        // But deletion *doesn't* reorder, it just updates the source list
        // setTodayTasks(updatedTasks.filter(t => t.isToday && !t.completed));
        // setQueueTasks(updatedTasks.filter(t => !t.isToday && !t.completed));
    }
  };

  const startEditing = (taskId: number, currentText: string, currentCategory?: string, currentDueDate?: Date | null) => {
    setEditingTaskId(taskId);
    setEditingText(currentText);
    setEditingCategory(currentCategory); // Set initial category for edit
    setEditingDueDate(currentDueDate || null); // Set initial due date for edit
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingTaskId !== null && editingText.trim()) {
      try {
        // Update text and category through the parent's onUpdateTask
        await onUpdateTask(editingTaskId, editingText.trim(), editingCategory);
        
        // Update due date directly through taskService
        if (editingDueDate !== null) {
          const task = allTasks.find(t => t.id === editingTaskId);
          if (task && task.dueDate !== editingDueDate) {
            await taskService.updateTask(editingTaskId, { dueDate: editingDueDate });
          }
        } else {
          // If due date was cleared, update it
          const task = allTasks.find(t => t.id === editingTaskId);
          if (task && task.dueDate !== null) {
            await taskService.updateTask(editingTaskId, { dueDate: null });
          }
        }
        
        // Refresh tasks to show the updates
        if (onRefreshTasks) {
          await onRefreshTasks();
        }
        
        // The update might change category, affecting filtering, refresh handles this
      } catch (error) {
        console.error("Failed to update task:", error);
      } finally {
        setEditingTaskId(null);
        setEditingText("");
        setEditingCategory(undefined);
        setEditingDueDate(null);
      }
    }
  };

  const cancelEditing = () => {
    setEditingTaskId(null);
    setEditingText("");
    setEditingCategory(undefined);
    setEditingDueDate(null);
  };

  // Filter tasks directly for rendering (completed tasks stay in their original position)
  const todayTasksAll = allTasks.filter(task => task.isToday && !task.completed);
  const todayTasksFiltered = todayTasksAll;

  const queueTasksAll = allTasks.filter(task =>
    !task.isToday &&
    !task.completed &&
    (!filterCategory || 
      (filterCategory === "Uncategorized" ? !task.category : task.category === filterCategory))
  );
  const queueTasksFiltered = queueTasksAll;

  // Memoize the allowed categories calculation to prevent unnecessary re-renders
  const { allowedCategories, hasUpcomingFilterableContent } = useMemo(() => {
    // Compute categories used in upcoming tasks
    const upcomingCategories = Array.from(new Set(
      allTasks
        .filter(task => !task.isToday && !task.completed && task.category)
        .map(task => task.category as string)
    )).sort(); // Sort categories alphabetically
    
    // Check if there are upcoming tasks without categories
    const hasUpcomingUncategorizedTasks = allTasks.some(task => 
      !task.isToday && !task.completed && !task.category
    );
    
    // Build the allowed categories list for the filter
    const allowedCategories = [...upcomingCategories];
    if (hasUpcomingUncategorizedTasks) {
      allowedCategories.unshift("Uncategorized"); // Keep "Uncategorized" at the top
    }
    
    // Only show category filter button if there are upcoming tasks with categories
    const hasUpcomingFilterableContent = upcomingCategories.length > 0;
    
    return { allowedCategories, hasUpcomingFilterableContent };
  }, [allTasks]); // Only recalculate when allTasks changes

  // Find the container a task belongs to based on the main list OR the container ID itself
  const findContainer = (id: number | string) => {
    // Check explicit container IDs first
    if (id === TODAY_CONTAINER_ID) return TODAY_CONTAINER_ID;
    if (id === QUEUE_CONTAINER_ID) return QUEUE_CONTAINER_ID;
    
    // Then check task properties if it's a task ID
    const task = allTasks.find(t => t.id === id);
    if (!task) return null;
    if (task.isToday) return TODAY_CONTAINER_ID;
    return QUEUE_CONTAINER_ID; // Default assumption
  };

  // getTaskById remains the same (conceptually)
  const getTaskById = (id: number | string): Task | undefined => {
      return allTasks.find(t => t.id === id);
  }

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(event.active.id);
    setDragStartContainer(findContainer(event.active.id)); // Track original container before any updates
    setIsDraggingOverFullToday(false);
    setShouldShakeDragItem(false);
    // Clear any existing shake timeout
    if (shakeTimeoutRef.current) {
      clearTimeout(shakeTimeoutRef.current);
      shakeTimeoutRef.current = null;
    }
  };

  // handleDragOver: dynamic intra-container reordering
  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) {
      setIsDraggingOverFullToday(false);
      return;
    }
    const activeId = active.id;
    const overId = over.id;
    if (activeId === overId) {
      setIsDraggingOverFullToday(false);
      return;
    }
    const activeContainer = findContainer(activeId);
    const overContainer = findContainer(overId);
    if (!activeContainer || !overContainer) {
      setIsDraggingOverFullToday(false);
      return;
    }

    // Check if dragging over Today when it's at capacity (5 non-completed tasks)
    const overIsTodayContainer = overId === TODAY_CONTAINER_ID || overContainer === TODAY_CONTAINER_ID;
    if (overIsTodayContainer && activeContainer !== TODAY_CONTAINER_ID) {
      const todayTaskCount = allTasks.filter(t => t.isToday && !t.completed).length;
      if (todayTaskCount >= 5) {
        if (dragFeedbackReason !== 'today-limit-reached') {
          setDragFeedbackReason('today-limit-reached');
        }
        // Trigger shake animation on first detection
        if (!isDraggingOverFullToday) {
          setShouldShakeDragItem(true);
          // Clear any existing timeout
          if (shakeTimeoutRef.current) {
            clearTimeout(shakeTimeoutRef.current);
          }
          // Reset shake after animation completes
          shakeTimeoutRef.current = setTimeout(() => {
            setShouldShakeDragItem(false);
            shakeTimeoutRef.current = null;
          }, 1450);
        }
        setIsDraggingOverFullToday(true);
        return; // Don't process reordering when over full container
      }
    }
    setIsDraggingOverFullToday(false);

    // Check if dragging active task to Upcoming - block it
    const overIsUpcomingContainer = overId === QUEUE_CONTAINER_ID || overContainer === QUEUE_CONTAINER_ID;
    const isDraggingActiveTask = activeContainer === TODAY_CONTAINER_ID && Number(activeId) === activeTaskId;
    if (overIsUpcomingContainer && isDraggingActiveTask) {
      if (isTimerRunning) {
        if (dragFeedbackReason !== 'active-task-locked') {
          setDragFeedbackReason('active-task-locked');
        }
        if (!isDraggingActiveToUpcoming) {
          setShouldShakeDragItem(true);
          if (shakeTimeoutRef.current) {
            clearTimeout(shakeTimeoutRef.current);
          }
          shakeTimeoutRef.current = setTimeout(() => {
            setShouldShakeDragItem(false);
            shakeTimeoutRef.current = null;
          }, 1450);
        }
        setIsDraggingActiveToUpcoming(true);
        return; // Don't process reordering
      }
    }

    if (!(overIsUpcomingContainer && isDraggingActiveTask && isTimerRunning)) {
      setIsDraggingActiveToUpcoming(false);
      setDragFeedbackReason('none');
    }

    // Intra-container move: visual feedback handled by dnd-kit, actual reorder in handleDragEnd
    // (Removed to prevent double-reorder jolt - handleDragEnd does the final reorder)
    
    // Cross-container visual feedback (no state change here)
    if (activeContainer !== overContainer) {
      // Cross-container dynamic reorder: simulate moving between containers
      const overIsItem = !!getTaskById(overId);
      const overIsTodayContainer = overId === TODAY_CONTAINER_ID;
      const overIsQueueContainer = overId === QUEUE_CONTAINER_ID;

      let targetContainerId: string | null = null;
      if (overIsItem) {
        targetContainerId = overContainer;
      } else if (overIsTodayContainer) {
        targetContainerId = TODAY_CONTAINER_ID;
      } else if (overIsQueueContainer) {
        targetContainerId = QUEUE_CONTAINER_ID;
      }

      if (targetContainerId) {
        const activeTask = getTaskById(activeId);
        if (activeTask) {
          const oldIndex = allTasks.findIndex(task => task.id === activeId);
          // Simulate status change
          const isMovingToToday = targetContainerId === TODAY_CONTAINER_ID;
          const updatedTask = { ...activeTask, isToday: isMovingToToday };
          let tasksAfterStatusChange = [...allTasks];
          tasksAfterStatusChange[oldIndex] = updatedTask;

          // Calculate new index for insertion
          let newIndex: number;
          if (!overIsItem) {
            if (targetContainerId === TODAY_CONTAINER_ID) {
              const lastTodayIndex = findLastIndex(tasksAfterStatusChange, (t) => t.isToday);
              newIndex = lastTodayIndex !== -1 ? lastTodayIndex + 1 : 0;
            } else {
              const lastQueueIndex = findLastIndex(
                tasksAfterStatusChange,
                (t) => !t.isToday && (!filterCategory || t.category === filterCategory)
              );
              newIndex = lastQueueIndex !== -1 ? lastQueueIndex + 1 : tasksAfterStatusChange.length;
            }
          } else {
            newIndex = tasksAfterStatusChange.findIndex(task => task.id === overId);
          }

          if (newIndex > -1) {
            // Adjust index if necessary
            const adjustedIndex = newIndex > oldIndex ? newIndex - 1 : newIndex;
            const reorderedTasks = arrayMove(tasksAfterStatusChange, oldIndex, adjustedIndex);
            onTasksReorder(reorderedTasks);
          }
        }
      }
    }
  };

  // Rewritten handleDragEnd to handle both inter and intra-list drops correctly
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    const originalStartContainer = dragStartContainer; // Capture before clearing
    setActiveDragId(null);
    setIsDraggingOverFullToday(false);
    setIsDraggingActiveToUpcoming(false);
    setShouldShakeDragItem(false);
    setDragFeedbackReason('none');
    setDragStartContainer(null);
    // Clear any existing shake timeout
    if (shakeTimeoutRef.current) {
      clearTimeout(shakeTimeoutRef.current);
      shakeTimeoutRef.current = null;
    }

    if (!over) {
        return;
    }

    const activeId = active.id;
    const overId = over.id;

    // Prevent updates if dropped on itself
    if (activeId === overId) {
        // Check if this was actually a cross-container move that handleDragOver already processed
        const activeTask = getTaskById(activeId);
        const currentContainer = activeTask?.isToday ? TODAY_CONTAINER_ID : QUEUE_CONTAINER_ID;
        const wasActuallyCrossContainer = originalStartContainer && originalStartContainer !== currentContainer;
        
        if (wasActuallyCrossContainer) {
            // handleDragOver already moved the task, but we still need to clear the filter if needed
            const isMovingToToday = currentContainer === TODAY_CONTAINER_ID;
            if (isMovingToToday && filterCategory) {
                const remainingFilteredTasks = allTasks.filter(
                    t => !t.isToday && 
                    !t.completed && 
                    t.id !== Number(activeId) &&
                    (filterCategory === "Uncategorized" ? !t.category : t.category === filterCategory)
                );
                if (remainingFilteredTasks.length === 0) {
                    setFilterCategory(undefined);
                }
            }
        }
        
        return;
    }

    const activeTask = getTaskById(activeId);
    if (!activeTask) {
        console.warn("handleDragEnd: Active task not found.");
        return;
    }

    const activeContainer = findContainer(activeId);
    // overId might be an item ID or a container ID
    const overIsItem = !!getTaskById(overId);
    const overIsTodayContainer = overId === TODAY_CONTAINER_ID;
    const overIsQueueContainer = overId === QUEUE_CONTAINER_ID;

    if (!activeContainer) {
        console.warn("handleDragEnd: Could not determine active container.");
        return;
    }

    // Determine the target container ID explicitly
    let targetContainerId: string | null = null;
    if (overIsItem) {
        targetContainerId = findContainer(overId);
    } else if (overIsTodayContainer) {
        targetContainerId = TODAY_CONTAINER_ID;
    } else if (overIsQueueContainer) {
        targetContainerId = QUEUE_CONTAINER_ID;
    }

    if (!targetContainerId) {
        console.warn("handleDragEnd: Could not determine target container from overId:", overId);
        return;
    }

    // Prevent adding to Today if it already has 5 non-completed tasks
    if (targetContainerId === TODAY_CONTAINER_ID && activeContainer !== TODAY_CONTAINER_ID) {
        const todayTaskCount = allTasks.filter(t => t.isToday && !t.completed).length;
        if (todayTaskCount >= 5) {
            return;
        }
    }

    // Prevent active task from being moved to Upcoming
    const isActiveTaskDropToUpcoming =
        targetContainerId === QUEUE_CONTAINER_ID &&
        activeContainer === TODAY_CONTAINER_ID &&
        Number(activeId) === activeTaskId;

    if (isActiveTaskDropToUpcoming && isTimerRunning) {
        return;
    }

    const oldIndex = allTasks.findIndex(task => task.id === activeId);
    if (oldIndex === -1) {
        console.warn("handleDragEnd: Old index not found.");
        return;
    }

    // --- Calculate newIndex ---
    let newIndex: number;
    if (overIsTodayContainer || overIsQueueContainer) {
        // Dropping onto the container area
        if (targetContainerId === TODAY_CONTAINER_ID) {
            const lastTodayIndex = findLastIndex(allTasks, (t) => t.isToday);
            newIndex = lastTodayIndex !== -1 ? lastTodayIndex + 1 : 0;
        } else { // QUEUE_CONTAINER_ID
            const lastQueueIndex = findLastIndex(
              allTasks,
              (t) => !t.isToday && (!filterCategory || t.category === filterCategory)
            );
            newIndex = lastQueueIndex !== -1 ? lastQueueIndex + 1 : allTasks.length;
        }
    } else {
        // Dropping onto a specific item
        newIndex = allTasks.findIndex(task => task.id === overId);
        if (newIndex === -1) {
            console.warn("handleDragEnd: Could not find new index for dropped over item:", overId);
            return; // Should not happen if overId is a valid item ID
        }
    }
    // Clamp index just in case
    newIndex = Math.max(0, Math.min(newIndex, allTasks.length));

    // --- Handle Movement ---
    if (activeContainer !== targetContainerId) {
        // Inter-container move
        const isMovingToToday = targetContainerId === TODAY_CONTAINER_ID;

        // Simulate status change and list modification locally for accurate final order
        const updatedTask = { ...activeTask, isToday: isMovingToToday };
        let tasksAfterStatusChange = [...allTasks];
        tasksAfterStatusChange[oldIndex] = updatedTask; // Update status in the temporary array
        
        // Calculate final position using the array with the updated status
        // Adjust index based on the simulated move
        const adjustedNewIndex = newIndex > oldIndex ? newIndex - 1 : newIndex;
        const finalOrderedTasks = arrayMove(tasksAfterStatusChange, oldIndex, adjustedNewIndex);

        try {
            const updatedTasks = await onSetTaskTodayStatus(Number(activeId), isMovingToToday);
            onTasksReorder(finalOrderedTasks); // Use the locally calculated order
            
            // Track task moved to today
            if (isMovingToToday) {
                posthog?.capture('task_moved_to_today', {
                  method: 'drag_and_drop',
                  had_category: !!activeTask.category
                });
            }
            
            if (isActiveTaskDropToUpcoming && !isTimerRunning && onActiveTaskMovedToUpcoming && updatedTasks) {
                await onActiveTaskMovedToUpcoming(Number(activeId), updatedTasks);
            }
        } catch (error) {
            console.error("Failed to update task today status on drop:", error);
            if (onRefreshTasks) await onRefreshTasks(); // Refresh on error
        }

    } else {
        // Intra-container move (within Today or within Queue)
        if (oldIndex !== newIndex) {
            const reorderedTasks = arrayMove(allTasks, oldIndex, newIndex);
            onTasksReorder(reorderedTasks);
        }
    }
    
    // Check if this was actually a cross-container move (handleDragOver already handled it)
    // and clear filter if needed
    const actuallyMovedToToday = originalStartContainer === QUEUE_CONTAINER_ID && 
                                  targetContainerId === TODAY_CONTAINER_ID;
    if (actuallyMovedToToday && filterCategory) {
        const remainingFilteredTasks = allTasks.filter(
            t => !t.isToday && 
            !t.completed && 
            t.id !== Number(activeId) &&
            (filterCategory === "Uncategorized" ? !t.category : t.category === filterCategory)
        );
        if (remainingFilteredTasks.length === 0) {
            setFilterCategory(undefined);
        }
    }
  };

  // Function to initiate category editing
  const initiateCategoryEdit = (categoryName: string) => {
    // For now, we'll handle editing inline in the CategoryFilterPopup
    // This function is kept for compatibility but doesn't do anything special
  };

  // Function to update all category state variables when a category is renamed
  const handleCategoryRenamed = async (oldName: string, newName: string) => {
    if (editingCategory === oldName) {
      setEditingCategory(newName);
    }
    if (inlineNewCategory === oldName) {
      setInlineNewCategory(newName);
    }
    if (filterCategory === oldName) {
      setFilterCategory(newName);
    }
    if (inlineNewTodayCategory === oldName) {
      setInlineNewTodayCategory(newName);
    }

    // Refresh tasks and update category lists
    if (onRefreshTasks) {
      await onRefreshTasks();
    }
    setCategoryPopupRefreshKey(prev => prev + 1);
  };

  // Effect to set cursor style when dragging over full Today section or active task to Upcoming
  useEffect(() => {
    if (isDraggingOverFullToday || isDraggingActiveToUpcoming) {
      document.body.style.cursor = 'not-allowed';
    } else {
      document.body.style.cursor = '';
    }
    return () => {
      document.body.style.cursor = '';
    };
  }, [isDraggingOverFullToday, isDraggingActiveToUpcoming]);

  // Inline styles for drag cursor and shake animation
  const todayStyles = `
    @keyframes shake {
      0%, 100% { transform: translateX(0); }
      12%, 37%, 62%, 87% { transform: translateX(-4px); }
      25%, 50%, 75% { transform: translateX(4px); }
    }
    .shake-animation {
      animation: shake 1.45s ease-in-out;
    }
    ${(isDraggingOverFullToday || isDraggingActiveToUpcoming) ? 'body, * { cursor: not-allowed !important; }' : ''}
  `;

  // Find the active task for the DragOverlay
  const activeTaskForOverlay = activeDragId ? getTaskById(activeDragId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetectionStrategy}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="max-w-md mx-auto">
        {/* Global styles for animations and drag cursor */}
        <style>{todayStyles}</style>

        {/* --- Today Section --- */}
        {/* Show header only if there are tasks in the today list */}
        {todayTasksFiltered.length > 0 && (
          <>
            <div className="flex justify-between items-center mb-4 group">
            <h4 className="today-header text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-dark-secondary">Today</h4>
            </div>
          </>
        )}

        {/* Inline Add Today Task Form */}
        {inlineAddingToday && (
          <>
            <div className="mb-4">
              <form onSubmit={handleAddTodayTask} className="bg-white dark:bg-dark-card rounded-lg border p-3 mb-2 border-blue-500 dark:border-primary-600">
                <textarea
                  ref={inlineTodayInputRef}
                  rows={1}
                  value={inlineNewTodayTask}
                  onChange={(e) => {
                    setInlineNewTodayTask(e.target.value);
                    autoGrow(e.currentTarget);
                  }}
                  onInput={(e) => autoGrow(e.currentTarget)}
                  placeholder="Add a task for today"
                  className="w-full border-0 focus:ring-0 p-0 text-gray-900 dark:text-dark-primary bg-transparent placeholder-gray-500 dark:placeholder-dark-secondary mb-2 whitespace-pre-wrap break-words resize-none overflow-hidden leading-relaxed"
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      handleCancelTodayAdd();
                    } else if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleAddTodayTask(e);
                    }
                  }}
                />
                {/* Category Selector and Buttons */}
                <div className="relative mt-2">
                   {/* Action Buttons Row */}
                   <div className="flex justify-between items-center mt-1">
                      {/* Left side: Tag button, Calendar button, and selected category/due date names */}
                      <div className="flex items-center space-x-1 min-w-0">
                          <CategoryModal
                              selectedCategory={inlineNewTodayCategory}
                              onChange={(category: string | undefined) => {
                                  setInlineNewTodayCategory(category);
                              }}
                              onCategoryRenamed={async (oldName: string, newName: string) => {
                                 await handleCategoryRenamed(oldName, newName);
                              }}
                          />
                          
                          <DueDateSelector
                              selectedDate={inlineNewTodayDueDate}
                              onChange={setInlineNewTodayDueDate}
                          />
                      </div>

                      {/* Right side: Cancel and Add Task buttons */}
                      <div className="flex space-x-2 flex-shrink-0">
                          <button
                              type="button"
                              onClick={handleCancelTodayAdd}
                              className="px-3 py-1 text-sm text-gray-600 dark:text-dark-secondary hover:text-gray-800 dark:hover:text-dark-primary"
                          >
                              Cancel
                          </button>
                          <button
                              type="submit"
                              disabled={!inlineNewTodayTask.trim()}
                              className="px-3 py-1 bg-blue-500 dark:bg-blue-600 text-white text-sm rounded hover:bg-blue-600 dark:hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                              Add Task
                          </button>
                      </div>
                   </div>
                </div>
              </form>
            </div>
          </>
        )}

        <SortableContext 
           items={todayTasksFiltered.map(task => task.id)}
           strategy={verticalListSortingStrategy} 
           id={TODAY_CONTAINER_ID}
        >
           {/* Wrap the content div with DroppableContainer */}
           <DroppableContainer 
              id={TODAY_CONTAINER_ID} 
              className={`space-y-2 mb-6 ${todayTasksFiltered.length === 0 ? 'min-h-[60px] border border-dashed border-gray-300 dark:border-dark-secondary bg-gray-100/50 dark:bg-dark-card/50 rounded-md flex items-center justify-center' : ''}`}
            >
                {todayTasksFiltered.length > 0 ? (
                    todayTasksFiltered.map((task) => (
                        <SortableTaskItem
                            key={task.id}
                            task={task}
                            onToggle={handleCheckboxChange}
                            onDelete={initiateDelete}
                            isActive={task.id === activeTaskId}
                            onSelect={onSelect}
                            onUpdateTask={onUpdateTask}
                            editingTaskId={editingTaskId}
                            onStartEditing={startEditing}
                            onCategoryRenamed={handleCategoryRenamed}
                            onEditSubmit={handleEditSubmit}
                            onCancelEdit={cancelEditing}
                            currentEditingText={editingText}
                            onEditingTextChange={setEditingText}
                            currentEditingCategory={editingCategory}
                            onEditingCategoryChange={setEditingCategory}
                            currentEditingDueDate={editingDueDate}
                            onEditingDueDateChange={setEditingDueDate}
                        />
                    ))
                ) : (
                    <p className="text-center text-sm text-gray-500 dark:text-dark-secondary px-4">
                        Drag up to 5 tasks to focus on today
                    </p>
                )}
           </DroppableContainer>
        </SortableContext>

        {/* --- Later Section Header & Controls --- */}
        <div className="flex justify-between items-center mb-4 group">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-dark-secondary">Upcoming</h4>
          <div className={`flex items-center space-x-2 transition-opacity duration-200 opacity-100`}>
            {/* Category Filter Popup - shown only if there are categorized upcoming tasks */}
            {hasUpcomingFilterableContent && (
            <CategoryFilterPopup
                selectedCategory={filterCategory}
                onChange={setFilterCategory}
                onCategoryRenamed={async (oldName: string, newName: string) => {
                    await handleCategoryRenamed(oldName, newName);
                }}
                initiateCategoryEdit={initiateCategoryEdit}
                refreshKey={categoryPopupRefreshKey}
                allowedCategories={allowedCategories}
                trigger={
                <button
                    onClick={() => {
                    cancelEditing(); // Cancel any ongoing task edit
                    // Popup handles its own open/close state via Headless UI
                    }}
                    className="text-gray-500 dark:text-gray-300 hover:text-gray-400 dark:hover:text-gray-400 focus:outline-none p-1 rounded-full"
                    aria-label="Filter tasks"
                >
                    {HiOutlineAdjustments({ className: "h-5 w-5" })}
                </button>
                }
            />
            )}
          </div>
        </div>

        {/* Filter Indicator Chip */}
        {filterCategory && (
          <div className="mb-3 flex items-center">
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryStyle(filterCategory).bg} ${getCategoryStyle(filterCategory).text}`}
            >
              {filterCategory}
              <button
                type="button"
                className={`ml-1.5 flex-shrink-0 inline-flex h-4 w-4 items-center justify-center rounded-full ${getCategoryStyle(filterCategory).text} opacity-70 hover:opacity-100 focus:outline-none focus:opacity-100`}
                onClick={() => setFilterCategory(undefined)}
                aria-label={`Remove ${filterCategory} filter`}
              >
                <svg className="h-2 w-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                  <path strokeLinecap="round" strokeWidth="1.5" d="M1 1l6 6m0-6L1 7" />
                </svg>
              </button>
            </span>
          </div>
        )}

        {/* Inline Add Task Form */}
        {inlineAdding && (
          <>
            <div className="mb-4">
              <form onSubmit={handleAddInlineTask} className="bg-white dark:bg-dark-card rounded-lg border p-3 mb-2 border-blue-500 dark:border-primary-600">
                <textarea
                  ref={inlineInputRef}
                  rows={1}
                  value={inlineNewTask}
                  onChange={(e) => {
                    setInlineNewTask(e.target.value);
                    autoGrow(e.currentTarget);
                  }}
                  onInput={(e) => autoGrow(e.currentTarget)}
                  placeholder="Add a task here"
                  className="w-full border-0 focus:ring-0 p-0 text-gray-900 dark:text-dark-primary bg-transparent placeholder-gray-500 dark:placeholder-dark-secondary mb-2 whitespace-pre-wrap break-words resize-none overflow-hidden leading-relaxed"
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      handleCancelInlineAdd();
                    } else if (e.key === 'Enter' && !e.shiftKey) { // Prevent adding on Shift+Enter
                      e.preventDefault();
                      handleAddInlineTask(e);
                    }
                  }}
                />
                {/* Category Selector and Buttons */}
                <div className="relative mt-2">
                   {/* Action Buttons Row */}
                   <div className="flex justify-between items-center mt-1">
                      {/* Left side: Tag button, Calendar button, and selected category/due date names */}
                      <div className="flex items-center space-x-1 min-w-0">
                          <CategoryModal
                              selectedCategory={inlineNewCategory}
                              onChange={(category: string | undefined) => {
                                  setInlineNewCategory(category);
                              }}
                              onCategoryRenamed={async (oldName: string, newName: string) => {
                                 await handleCategoryRenamed(oldName, newName);
                              }}
                          />
                          
                          <DueDateSelector
                              selectedDate={inlineNewDueDate}
                              onChange={setInlineNewDueDate}
                          />
                      </div>

                      {/* Right side: Cancel and Add Task buttons */}
                      <div className="flex space-x-2 flex-shrink-0">
                        {/* Show cancel only if there are queued tasks */}
                         {queueTasksFiltered.length > 0 && (
                          <button
                              type="button"
                              onClick={handleCancelInlineAdd}
                              className="px-3 py-1 text-sm text-gray-600 dark:text-dark-secondary hover:text-gray-800 dark:hover:text-dark-primary"
                          >
                              Cancel
                          </button>
                          )}
                          <button
                              type="submit"
                              disabled={!inlineNewTask.trim()} // Disable if input is empty
                              className="px-3 py-1 bg-blue-500 dark:bg-blue-600 text-white text-sm rounded hover:bg-blue-600 dark:hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                              Add Task
                          </button>
                      </div>
                   </div>
                </div>
              </form>
            </div>
            {/* OR separator under inline-add when queue is empty AND today has tasks */}
            {queueTasksFiltered.length === 0 && todayTasksFiltered.length > 0 && (
            <div className="flex items-center justify-center my-4">
              <hr className="flex-grow border-gray-300 dark:border-dark-secondary" />
              <span className="px-2 text-sm text-gray-500 dark:text-dark-secondary">or</span>
              <hr className="flex-grow border-gray-300 dark:border-dark-secondary" />
            </div>
            )}
          </>
        )}

        {/* Upcoming Task List */}
        <div className="mt-4">
          {/* Only show drop zone if there are any tasks in Today or Upcoming*/}
          {(queueTasksFiltered.length > 0 || (todayTasksFiltered.length > 0 && !filterCategory)) && (
            <SortableContext
              items={queueTasksFiltered.map(task => task.id)}
              strategy={verticalListSortingStrategy}
              id={QUEUE_CONTAINER_ID}
            >
              <DroppableContainer
                id={QUEUE_CONTAINER_ID}
                className={`space-y-2 ${queueTasksFiltered.length === 0 && !filterCategory ? 'min-h-[60px] border border-dashed border-gray-300 dark:border-dark-secondary bg-gray-100/50 dark:bg-dark-card/50 rounded-md flex items-center justify-center' : ''}`}
              >
                {queueTasksFiltered.length > 0
                  ? queueTasksFiltered.map((task) => (
                      <SortableTaskItem
                        key={task.id}
                        task={task}
                        onToggle={handleCheckboxChange}
                        onDelete={initiateDelete}
                        isActive={task.id === activeTaskId}
                        onSelect={onSelect}
                        onUpdateTask={onUpdateTask}
                        editingTaskId={editingTaskId}
                        onStartEditing={startEditing}
                        onCategoryRenamed={handleCategoryRenamed}
                        onEditSubmit={handleEditSubmit}
                        onCancelEdit={cancelEditing}
                        currentEditingText={editingText}
                        onEditingTextChange={setEditingText}
                        currentEditingCategory={editingCategory}
                        onEditingCategoryChange={setEditingCategory}
                        currentEditingDueDate={editingDueDate}
                        onEditingDueDateChange={setEditingDueDate}
                      />
                    ))
                  : todayTasksFiltered.length > 0 && !filterCategory && (
                      <p className="text-center text-sm text-gray-500 dark:text-dark-secondary px-4">
                        Drag an existing task here
                      </p>
                    )}
              </DroppableContainer>
            </SortableContext>
          )}
        </div>

        {/* Delete Confirmation Dialog */}
      <Transition appear show={showDeleteConfirm} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-[1100]"
            onClose={() => {
              setShowDeleteConfirm(false);
              setTaskToDelete(null);
            }}
          >
            {/* ... Dialog overlay and content ... */}
             <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                >
                <div className="fixed inset-0 bg-black/25 dark:bg-black/50" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                >
                    <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-dark-card p-6 text-left align-middle shadow-xl transition-all">
                    <div className="flex items-start gap-3">
                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30 sm:mx-0 sm:h-10 sm:w-10">
                        <ExclamationTriangleIcon
                            className="h-6 w-6 text-red-600 dark:text-red-400"
                            aria-hidden="true"
                        />
                        </div>
                        <div className="mt-0 text-center sm:text-left w-full">
                        <Dialog.Title
                            as="h3"
                            className="text-lg font-medium leading-6 text-gray-900 dark:text-dark-primary"
                        >
                        {taskToDelete === -1
                            ? "Delete All Completed Tasks?"
                            : "Delete Task?"}
                        </Dialog.Title>
                        <div className="mt-2">
                            <p className="text-base text-gray-500 dark:text-dark-secondary">
                            {taskToDelete === -1
                                ? "Are you sure you want to delete all completed tasks? This action cannot be undone."
                                : "Are you sure you want to delete this task? This action cannot be undone."
                            }
                            </p>
                        </div>
                        </div>
                    </div>

                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                        <button
                        type="button"
                        className="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-dark-card sm:ml-3 sm:w-auto sm:text-sm"
                        onClick={confirmDelete}
                        >
                        Delete
                        </button>
                        <button
                        type="button"
                        className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card px-4 py-2 text-base font-medium text-gray-700 dark:text-dark-secondary shadow-sm hover:bg-gray-50 dark:hover:bg-dark-hover focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-dark-card sm:mt-0 sm:w-auto sm:text-sm"
                        onClick={() => {
                            setShowDeleteConfirm(false);
                            setTaskToDelete(null);
                        }}
                        >
                        Cancel
                        </button>
                    </div>
                    </Dialog.Panel>
                </Transition.Child>
                </div>
            </div>
          </Dialog>
        </Transition>
      </div>

      {/* Drag Overlay Implementation */}
      <DragOverlay dropAnimation={null}>
        {activeTaskForOverlay ? (
          /* Render the dragged task under the cursor with shake animation */
          <div className={`relative ${shouldShakeDragItem ? 'shake-animation' : ''}`}>
            <SortableTaskItem
              key={`overlay-${activeTaskForOverlay!.id}`}
              task={activeTaskForOverlay!}
              isActive={activeTaskForOverlay!.id === activeTaskId}
              editingTaskId={null}
              onToggle={async () => {}}
              onDelete={() => {}}
              onSelect={() => {}}
              onUpdateTask={async () => {}}
              onStartEditing={() => {}}
              onCategoryRenamed={async () => {}}
              onEditSubmit={async () => {}}
              onCancelEdit={() => {}}
              currentEditingText=""
              onEditingTextChange={() => {}}
              currentEditingCategory={undefined}
              onEditingCategoryChange={() => {}}
              currentEditingDueDate={null}
              onEditingDueDateChange={() => {}}
            />
            <DragFeedbackTooltip reason={dragFeedbackReason} />
          </div>
        ) : null}
      </DragOverlay>

    </DndContext>
  );
};

export default TaskList;
